import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router'

import classnames from 'classnames'
import {
  ArrowBackIcon,
  ChevronRightIcon,
  RestaurantIcon
} from '@toasttab/buffet-pui-icons'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'
import { useOOLocations } from 'public/components/online_ordering/FulfillmentContext'
import { useRestaurantRoutes } from 'shared/components/common/restaurant_routes/RestaurantRoutesContext'

import { ModalCloseButton } from 'shared/components/common/modal'
import ChangeLocationModal from 'public/components/default_template/online_ordering/location_selection/ChangeLocationModal'

import { LeftMenuPanelProps } from './LeftMenuPanel'
import { filterMenuGroupsByItemCount } from './shared'
import { MenuGroup } from './types'
import { t } from 'src/translations'
import { navigateToCategory } from './urlFilterUtils'

type NavigationState = {
  breadcrumb: MenuGroup[] // Path of selected items from root to current level
  title: string
}

type AnimationState = {
  isAnimating: boolean
  direction: 'forward' | 'backward' | null
  currentContent: React.ReactNode
  nextContent: React.ReactNode
}

export const MobileLeftNav = ({
  isOpen,
  menuGroups,
  selectedGroup,
  initialSelectedChild,
  currentCategorySlug,
  onClose,
  onGroupSelect,
  setFilters
}: LeftMenuPanelProps) => {
  const history = useHistory()
  const { ecommPath } = useRestaurantRoutes()
  const [navState, setNavState] = useState<NavigationState>({
    breadcrumb: [],
    title: t('mobile-left-nav.browse-products')
  })

  const [animationState, setAnimationState] = useState<AnimationState>({
    isAnimating: false,
    direction: null,
    currentContent: null,
    nextContent: null
  })

  const { selectedLocation } = useRestaurant()
  const ooLocations = useOOLocations()
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false)

  const showLocationSelector = ooLocations?.length > 1 && selectedLocation

  const buildFilterString = (breadcrumb: MenuGroup[]): string => {
    return breadcrumb.map((item) => item.guid).join(':')
  }

  const applyFilter = (filterString: string) => {
    setFilters(new Set([filterString]))
    onClose()
  }

  useEffect(() => {
    if (selectedGroup) {
      // If initialSelectedChild is provided, drill down to that level
      const breadcrumb = initialSelectedChild
        ? [selectedGroup, initialSelectedChild]
        : [selectedGroup]
      const title = initialSelectedChild
        ? initialSelectedChild.name
        : selectedGroup.name

      setNavState({
        breadcrumb,
        title
      })
    } else {
      setNavState({
        breadcrumb: [],
        title: t('mobile-left-nav.browse-products')
      })
    }
  }, [selectedGroup, initialSelectedChild])

  if (!isOpen) return null

  const animateToNewState = (
    newState: NavigationState,
    direction: 'forward' | 'backward'
  ) => {
    const currentContent = renderContentForState(navState)
    const nextContent = renderContentForState(newState)

    setAnimationState({
      isAnimating: true,
      direction,
      currentContent,
      nextContent
    })

    // Update nav state after animation starts
    setTimeout(() => {
      setNavState(newState)

      // Complete animation after CSS transition
      setTimeout(() => {
        setAnimationState({
          isAnimating: false,
          direction: null,
          currentContent: null,
          nextContent: null
        })
      }, 300) // Match CSS transition duration
    }, 50)
  }

  const handleItemClick = (item: MenuGroup) => {
    if (!item.children || item.children.length === 0) {
      // Leaf node
      if (item.slug) {
        navigateToCategory(item, ecommPath, history, onClose)
      } else {
        const filterPath = [...navState.breadcrumb, item]
        applyFilter(buildFilterString(filterPath))
      }
    } else {
      // Has children - drill down
      const newBreadcrumb = [...navState.breadcrumb, item]
      const newState = {
        breadcrumb: newBreadcrumb,
        title: item.name
      }
      animateToNewState(newState, 'forward')

      // Call onGroupSelect only for top-level selections
      if (navState.breadcrumb.length === 0) {
        onGroupSelect?.(item)
      }
    }
  }

  const handleBackClick = () => {
    if (navState.breadcrumb.length > 1) {
      // Go back one level
      const newBreadcrumb = navState.breadcrumb.slice(0, -1)
      const newState = {
        breadcrumb: newBreadcrumb,
        title:
          newBreadcrumb[newBreadcrumb.length - 1]?.name ||
          t('mobile-left-nav.browse-products') ||
          'Browse Products'
      }
      animateToNewState(newState, 'backward')
    } else if (navState.breadcrumb.length === 1) {
      // Go back to top level
      const newState = {
        breadcrumb: [],
        title: t('mobile-left-nav.browse-products')
      }
      animateToNewState(newState, 'backward')
    }
  }

  const handleViewAllClick = () => {
    if (navState.breadcrumb.length > 0) {
      const currentGroup = navState.breadcrumb[navState.breadcrumb.length - 1]
      if (currentGroup) {
        if (currentGroup.slug) {
          navigateToCategory(currentGroup, ecommPath, history, onClose)
        } else {
          applyFilter(buildFilterString(navState.breadcrumb))
        }
      }
    }
  }

  const getCurrentItems = (state: NavigationState): MenuGroup[] => {
    if (state.breadcrumb.length === 0) {
      return filterMenuGroupsByItemCount(menuGroups)
    } else {
      const lastItem = state.breadcrumb[state.breadcrumb.length - 1]
      return lastItem?.children
        ? filterMenuGroupsByItemCount(lastItem.children)
        : []
    }
  }

  const renderContentForState = (state: NavigationState) => {
    const isTopLevel = state.breadcrumb.length === 0
    const currentItems = getCurrentItems(state)
    const currentParent = state.breadcrumb[state.breadcrumb.length - 1]

    return (
      <div className='mobileNavItems'>
        {!isTopLevel && (
          <button className='viewAllButton' onClick={handleViewAllClick}>
            {t('mobile-left-nav.view-all', { name: currentParent?.name })}
          </button>
        )}
        {currentItems.length > 0 ? (
          currentItems.map((item) => {
            return (
              <button
                key={item.guid}
                className={classnames('mobileNavItem', {
                  currentCategory: item.slug === currentCategorySlug
                })}
                onClick={() => handleItemClick(item)}
              >
                <span className='itemName'>{item.name}</span>
                {item.children &&
                  filterMenuGroupsByItemCount(item.children).length > 0 && (
                    <ChevronRightIcon
                      size='xs'
                      accessibility='semantic'
                      aria-label={t('mobile-left-nav.has-subcategories')}
                      className='chevronIcon'
                      data-testid='chevron-right'
                    />
                  )}
              </button>
            )
          })
        ) : (
          <p className='noItems'>
            {isTopLevel
              ? t('mobile-left-nav.no-categories-available')
              : t('mobile-left-nav.no-subcategories-available')}
          </p>
        )}
      </div>
    )
  }

  const renderContent = () => {
    if (animationState.isAnimating) {
      return (
        <div className='animationContainer'>
          <div
            className={`contentSlide current ${
              animationState.direction === 'forward'
                ? 'slideOutLeft'
                : 'slideOutRight'
            }`}
          >
            {animationState.currentContent}
          </div>
          <div
            className={`contentSlide next ${
              animationState.direction === 'forward'
                ? 'slideInRight'
                : 'slideInLeft'
            }`}
          >
            {animationState.nextContent}
          </div>
        </div>
      )
    }

    return renderContentForState(navState)
  }

  return (
    <>
      {!isLocationModalOpen && (
        <div
          className='panelOverlay'
          onClick={onClose}
          data-testid='panel-overlay'
        >
          <div
            className='leftPanel mobile'
            onClick={(e) => e.stopPropagation()}
            role='dialog'
            aria-label={t('mobile-left-nav.menu-nav')}
          >
            <div className='panelHeader'>
              <div className='headerLeft'>
                {navState.breadcrumb.length > 0 && (
                  <button className='navBackButton' onClick={handleBackClick}>
                    <ArrowBackIcon />
                  </button>
                )}
                <h3>{navState.title}</h3>
              </div>
              <div className='modalHeader'>
                <ModalCloseButton onClose={onClose} />
              </div>
            </div>

            <div className='panelContent'>
              <div className='scrollContent'>{renderContent()}</div>
              {showLocationSelector && (
                <button
                  className='mobileLocationSelector'
                  onClick={() => setIsLocationModalOpen(true)}
                  data-testid='mobile-location-selector-button'
                  aria-label={`Change location from ${selectedLocation.name}`}
                >
                  <div className='locationInfo'>
                    <RestaurantIcon
                      accessibility='decorative'
                      className='restaurantIcon'
                    />
                    <div className='locationDetails'>
                      <span className='locationName'>
                        {selectedLocation.name}
                      </span>
                    </div>
                  </div>
                  <ChevronRightIcon
                    accessibility='decorative'
                    className='chevronIcon'
                  />
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {showLocationSelector && (
        <ChangeLocationModal
          isOpen={isLocationModalOpen}
          onClose={() => {}}
          onConfirmClose={() => setIsLocationModalOpen(false)}
        />
      )}
    </>
  )
}
